import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  Check,
  Pencil,
  Tag,
  Trash2,
} from 'lucide-react';
import {
  formatDateBR,
  todaySP,
  type RecurrenceScope,
  type Transaction,
  type TransactionResponse,
} from '@lira/shared';
import { apiDelete, apiGet, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { useComposeStore } from '@/stores/compose';
import { toast } from '@/stores/toast';
import { ConfirmDialog, Modal, MoneyText, Skeleton } from '@/components/ui';
import { RecurrenceScopeDialog } from './RecurrenceScopeDialog';
import {
  effectuationSuccessMessage,
  hasEffectuationSource,
  transferRouteLabel,
} from './transfer-presentation';

interface TransactionDetailsModalProps {
  open: boolean;
  transactionId: string | null;
  initialTransaction?: Transaction | null;
  onClose: () => void;
}

/**
 * Leitura e ações de um lançamento, compartilhada pelo calendário e pelas listas
 * desktop/mobile. A ação financeira só existe para contas pendentes já vencidas;
 * compras de cartão são liquidadas exclusivamente pela fatura.
 */
export function TransactionDetailsModal({
  open,
  transactionId,
  initialTransaction = null,
  onClose,
}: TransactionDetailsModalProps) {
  const queryClient = useQueryClient();
  const openEdit = useComposeStore((state) => state.openEdit);
  const [deleting, setDeleting] = useState(false);

  const query = useQuery({
    queryKey: ['transaction-details', transactionId],
    queryFn: () => apiGet<TransactionResponse>(`/transactions/${transactionId}`),
    enabled: open && !!transactionId,
    initialData: initialTransaction && initialTransaction.id === transactionId
      ? { transaction: initialTransaction }
      : undefined,
  });
  const transaction = query.data?.transaction ?? null;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['expense-calendar'] });
    void queryClient.invalidateQueries({ queryKey: ['cards'] });
  };

  const effectuate = useMutation({
    mutationFn: (id: string) => apiPost<TransactionResponse>(`/transactions/${id}/effectuate`),
    onSuccess: (response) => {
      queryClient.setQueryData(['transaction-details', response.transaction.id], response);
      invalidate();
      toast(effectuationSuccessMessage(response.transaction.type), 'success');
    },
    onError: (error) => toast(errorMessage(error), 'error'),
  });

  const remove = useMutation({
    mutationFn: ({ id, scope }: { id: string; scope: RecurrenceScope }) =>
      apiDelete(`/transactions/${id}?scope=${scope}`),
    onSuccess: () => {
      invalidate();
      setDeleting(false);
      onClose();
      toast('Lançamento excluído.', 'success');
    },
    onError: (error) => toast(errorMessage(error), 'error'),
  });

  const canSettle = !!transaction
    && !transaction.effectuated
    && !transaction.cardId
    && hasEffectuationSource(transaction)
    && transaction.date <= todaySP()
    && (transaction.type === 'expense' || transaction.type === 'income' || transaction.type === 'transfer');

  const settleLabel = transaction?.type === 'transfer'
    ? 'Efetivar'
    : transaction?.type === 'income' ? 'Receber' : 'Pagar';
  const sourceName = transaction?.type === 'transfer'
    ? transferRouteLabel(transaction) ?? 'Transferência'
    : transaction?.cardName ?? transaction?.accountName ?? 'Sem conta';

  return (
    <>
      <Modal open={open} onClose={onClose} title="Detalhes do lançamento" maxWidth={560}>
        {query.isLoading && <div className="space-y-3 py-2"><Skeleton className="h-16" /><Skeleton className="h-24" /></div>}
        {query.isError && (
          <p className="rounded-lg bg-expense-weak px-4 py-5 text-sm text-expense">
            {errorMessage(query.error)}
          </p>
        )}
        {transaction && (
          <div className="min-w-0 space-y-5">
            <div className="rounded-xl bg-canvas p-4">
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="break-words text-lg font-semibold text-ink">
                    {transaction.description || transaction.categoryName || 'Lançamento'}
                  </p>
                  <p className="mt-1 text-sm text-muted">
                    {transaction.type === 'transfer'
                      ? (transaction.effectuated ? 'Efetivada' : 'Pendente')
                      : transaction.type === 'income'
                        ? (transaction.effectuated ? 'Recebida' : 'A receber')
                        : (transaction.effectuated ? 'Paga' : 'Pendente')}
                    {transaction.cardId ? ' · Compra no cartão' : ''}
                  </p>
                </div>
                <MoneyText cents={transaction.amountCents} colorBySign={transaction.type !== 'transfer'} showPositiveSign={transaction.type === 'income'} bold className="shrink-0 text-lg" />
              </div>
            </div>

            <dl className="grid min-w-0 gap-3 text-sm sm:grid-cols-2">
              <div className="min-w-0 rounded-lg border border-border p-3">
                <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted"><CalendarDays className="size-4" />Data</dt>
                <dd className="mt-1 font-medium text-ink">{formatDateBR(transaction.date)}</dd>
              </div>
              <div className="min-w-0 rounded-lg border border-border p-3">
                <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                  {transaction.type === 'transfer' ? 'Contas' : transaction.cardId ? 'Cartão' : 'Conta'}
                </dt>
                <dd className="mt-1 truncate font-medium text-ink">{sourceName}</dd>
              </div>
              <div className="min-w-0 rounded-lg border border-border p-3">
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">Categoria</dt>
                <dd className="mt-1 truncate font-medium text-ink">{transaction.categoryName ?? (transaction.type === 'transfer' ? 'Transferência' : 'Sem categoria')}</dd>
              </div>
              <div className="min-w-0 rounded-lg border border-border p-3">
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">Tipo</dt>
                <dd className="mt-1 font-medium text-ink">
                  {transaction.type === 'expense' ? 'Despesa' : transaction.type === 'income' ? 'Receita' : 'Transferência'}
                </dd>
              </div>
            </dl>

            {transaction.note && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">Observação</p>
                <p className="mt-1 whitespace-pre-wrap break-words text-sm text-ink">{transaction.note}</p>
              </div>
            )}
            {transaction.tags.length > 0 && (
              <div className="flex flex-wrap gap-2" aria-label="Tags">
                {transaction.tags.map((item) => (
                  <span key={item.id} className="inline-flex items-center gap-1 rounded-full bg-primary-weak px-2.5 py-1 text-xs font-medium text-primary">
                    <Tag className="size-3" />{item.name}
                  </span>
                ))}
              </div>
            )}

            <div className="flex min-w-0 flex-wrap justify-end gap-2 border-t border-border pt-4">
              {canSettle && (
                <button
                  type="button"
                  disabled={effectuate.isPending}
                  onClick={() => effectuate.mutate(transaction.id)}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full bg-income px-4 text-sm font-semibold text-white disabled:opacity-60"
                >
                  <Check className="size-4" />{settleLabel}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  openEdit(transaction);
                }}
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border px-4 text-sm font-semibold text-ink hover:bg-canvas"
              >
                <Pencil className="size-4" />Editar
              </button>
              <button
                type="button"
                onClick={() => setDeleting(true)}
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-expense/30 px-4 text-sm font-semibold text-expense hover:bg-expense-weak"
              >
                <Trash2 className="size-4" />Excluir
              </button>
            </div>
          </div>
        )}
      </Modal>

      {transaction?.recurrenceId ? (
        <RecurrenceScopeDialog
          open={deleting}
          title="Excluir lançamento recorrente"
          confirmLabel="Excluir"
          danger
          loading={remove.isPending}
          onConfirm={(scope) => remove.mutate({ id: transaction.id, scope })}
          onCancel={() => setDeleting(false)}
        />
      ) : (
        <ConfirmDialog
          open={deleting}
          title="Excluir lançamento"
          message="Esta ação não pode ser desfeita."
          loading={remove.isPending}
          onConfirm={() => transaction && remove.mutate({ id: transaction.id, scope: 'one' })}
          onCancel={() => setDeleting(false)}
        />
      )}
    </>
  );
}
